import {
  BIZ_NO_DIGITS,
  CORP_NO_DIGITS,
  TEL_MAX_DIGITS,
  stripNumberSeparators,
} from '@paytms/shared';
import { formatPhone } from './phone-format';

/** 숫자만 남긴다 — 사업자번호 등 표시 포맷 입력값의 저장용 정규화 */
export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/** 입력창에서 as-you-type 포맷을 거는 값의 종류 — 전화·사업자번호·법인번호 */
export type FormatKind = 'phone' | 'bizNo' | 'corpNo';

/** 종류별 최대 자릿수 — 서버 검증 상한(shared 상수)에서 파생. 넘으면 포맷하지 않을 뿐 자르지 않는다 */
export const FORMAT_MAX_DIGITS: Record<FormatKind, number> = {
  phone: TEL_MAX_DIGITS,
  bizNo: BIZ_NO_DIGITS,
  corpNo: CORP_NO_DIGITS,
};

const FORMATTERS: Record<FormatKind, (digits: string) => string> = {
  phone: formatPhone,
  bizNo: formatBizNo,
  corpNo: formatCorpNo,
};

/**
 * 입력창 한 글자 입력마다 부르는 정규화 — 서버 DTO Transform과 **같은 규칙**(`stripNumberSeparators`):
 * 구분 기호(공백·하이픈·괄호·마침표)만 걷어낸다. `digits`는 폼 컨트롤에 넣을 값, `display`는
 * 입력창에 다시 써 넣을 표시값이다.
 *
 * 숫자가 아닌 것을 전부 지우거나 상한으로 자르지 **않는다** — `+82-10-1234-5678`을
 * `82101234567`로 만들어 서버를 통과시키거나, 붙여넣은 마지막 자리를 조용히 없애면 안 된다
 * (2026-09-15 리뷰 C1). 글자·`+`·초과 자릿수는 입력창에 그대로 보이고, 저장하면 서버가 400을 준다.
 * 정리값이 숫자만이고 상한 이하일 때만 하이픈을 붙인다.
 */
export function applyFormat(kind: FormatKind, raw: string): { digits: string; display: string } {
  const digits = stripNumberSeparators(raw);
  const formattable = /^\d+$/.test(digits) && digits.length <= FORMAT_MAX_DIGITS[kind];
  return { digits, display: formattable ? FORMATTERS[kind](digits) : digits };
}

/**
 * 사업자등록번호 표시 포맷 000-00-00000 (10자리 아닐 땐 원본 유지).
 *
 * `undefined`도 받는다 — 서버가 아직 그 필드를 안 내려주는 배포 시점(계약이 프론트에
 * 먼저 반영된 순간)에 그리드 셀에서 이 함수가 불리면, 타입상 올 수 없는 값이라도
 * 런타임에는 들어온다. 3배치 e2e에서 실제로 `.replace` 크래시로 배포 대상 그리드가
 * 통째로 렌더되지 않았다(행 자체가 사라져 선택 체크박스도 없었다).
 */
export function formatBizNo(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  // 전화와 같은 규칙 — 구분 기호만 걷어내고, 숫자만·자릿수 일치일 때만 포맷 (`+8884300649`는 원문)
  const digits = stripNumberSeparators(value);
  if (!/^\d+$/.test(digits) || digits.length !== BIZ_NO_DIGITS) {
    return value;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/** 법인등록번호 표시 포맷 000000-0000000 (13자리 아닐 땐 원본 유지) — nullish 처리는 formatBizNo와 동일 */
export function formatCorpNo(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const digits = stripNumberSeparators(value);
  if (!/^\d+$/.test(digits) || digits.length !== CORP_NO_DIGITS) {
    return value;
  }
  return `${digits.slice(0, 6)}-${digits.slice(6)}`;
}
